import { useContext } from "react"
import useUrlParams from "./useUrlParams";
import useUrlNavigation from "./useUrlNavigation";
import { showToastMessage } from "../../../utils/commonFunctions";
import SimulateService from "../../../service/SimulateService";
import useLoader from "./useLoader";
import { CommandCenterStateProviderContext } from "../context";
import { Orbit, ScenarioResponseType } from "../types";
import * as Cesium from 'cesium';
import { isOperateMode, isSummaryMode } from "./utils";
import { SCENARIO_DEPLOYMENT_TYPES } from "../../simulate/constants";

const useScenario = () => {
    const context = useContext(CommandCenterStateProviderContext);
    const { setLoading } = useLoader();
    const { navigate_dashboard } = useUrlNavigation();
    const { mission_id, scenario_id, dashboard } = useUrlParams()

    if (context === undefined) {
        throw new Error('useScenario must be used within a ScenarioProvider');
    }
    const { scenario, set_scenario, deploymentSessionType, setDeploymentSessionType, simulation_time, set_simulation_time, telemetry_time, set_telemetry_time, grafana, set_grafana, unDeployLoader, setUnDeployLoader, grpGrafanaData, setGrpGrafanaData, set_satellites } = context;

    /**
     * Undeploy scenario API call
     * Used by: simulation mode, quick-deploy mode, and TRUETWIN_COMMAND_CENTER mode
     * API: POST /test/${missionId}/scenario/${scenarioId}/undeployment
     * Note: This is the same API used in simulation mode, not the operate mode (which doesn't have undeploy)
     */
    const undeploy_scenario = async () => {
        setLoading('undeploy', true)
        try {
            await SimulateService.unDeployScenario(mission_id, scenario_id)
            // Undeploy is asynchronous. A fulfilled request means it was accepted;
            // completion is reflected later by scenario-list polling.
            navigate_dashboard()
        } catch (err: any) {
            showToastMessage('error', err?.response?.data?.message ?? "Something went wrong During Undeploy")
        } finally {
            setLoading('undeploy', false)
        }
    }

    const get_simulation_time = (scenario: ScenarioResponseType, orbitData: Orbit, viewer?: Cesium.Viewer) => {
        const satelliteGroups = orbitData?.satellites?.[0]?.satelliteGroups?.[0]
        const interval: string | undefined = satelliteGroups?.visualizationData?.documentMetadata?.clock?.interval
        const multiplier: number | undefined = orbitData?.documentMetadata?.clock?.multiplier
        const operateMode = isOperateMode(dashboard, scenario)
        const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase()
        const useScenarioTimeApi = deploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER
        const summaryMode = isSummaryMode(dashboard, scenario)
        const shouldUseOperateLogic = operateMode && !useScenarioTimeApi && !summaryMode

        let simulation_start_time: number
        let simulation_end_time: number

        if (summaryMode && interval) {
            const [startStr, endStr] = interval.split('/')
            simulation_start_time = new Date(startStr).getTime() / 1000
            simulation_end_time   = new Date(endStr).getTime() / 1000
        } else if (shouldUseOperateLogic) {
            simulation_start_time = scenario?.scenarioStartTime
            simulation_end_time   = scenario?.scenarioStartTime + (scenario?.scenarioDuration * 60)
        } else {
            simulation_start_time = scenario?.simulationStartTime
            simulation_end_time   = scenario?.simulationStartTime + (scenario?.simulationDuration * 60)
        }

        let simulation_duration = (simulation_end_time - simulation_start_time) / 60
        let simulation_duration_second = simulation_duration * 60
        let simulation_end_time_milisec = simulation_end_time * 1000
        let simulation_start_time_milisec = simulation_start_time * 1000

        if (viewer && viewer?.clock && shouldUseOperateLogic) {
            const clock = viewer?.clock;
            const currentTime = clock?.currentTime;
            const baseTime = Cesium.JulianDate.toDate(currentTime).getTime()
            const epochTime = multiplier != null && multiplier > 0 ? baseTime + (multiplier * 1000) : baseTime
            if (epochTime >= simulation_end_time_milisec) {
                simulation_start_time = Math.round((epochTime / 1000) - (simulation_duration_second / 2))
                simulation_end_time = simulation_start_time + simulation_duration_second
                simulation_end_time_milisec = simulation_end_time * 1000
            }
        }

        set_simulation_time({
            start_time: simulation_start_time,
            end_time: simulation_end_time,
            duration: simulation_duration,
            manual_change: false
        })
        return {
            simulation_start_time,
            simulation_end_time,
            simulation_duration,
            simulation_end_time_milisec,
            simulation_start_time_milisec,
            manual_change: false
        }

    }

    const getUpdatedSimulationTime = (scenario: ScenarioResponseType, time: number, currentTime: number, simulationDuration: number) => {
        const simulationDurationSecond = simulationDuration * 60
        const simulationTime = Math.round(time - (simulationDurationSecond / 2))
        const newSimulationStartTime = simulationTime < scenario?.scenarioStartTime ? scenario?.scenarioStartTime : simulationTime
        const newSimulationEndTime = newSimulationStartTime + simulationDurationSecond
        const manualChange = currentTime >= newSimulationStartTime && currentTime <= newSimulationEndTime
        set_simulation_time({
            start_time: newSimulationStartTime,
            end_time: newSimulationEndTime,
            duration: simulationDuration,
            manual_change: manualChange
        })
        return {
            start_time: newSimulationStartTime,
            end_time: newSimulationEndTime,
            duration: simulationDuration,
            manual_change: manualChange
        }
    }


    return {
        scenario,
        set_scenario,
        deploymentSessionType,
        setDeploymentSessionType,
        telemetry_time,
        simulation_time,
        set_telemetry_time,
        set_simulation_time,
        grafana,
        set_grafana,
        unDeployLoader,
        undeploy_scenario,
        get_simulation_time,
        getUpdatedSimulationTime,
        setUnDeployLoader,
        grpGrafanaData,
        setGrpGrafanaData,
        set_satellites,
    };
}

export default useScenario
